import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import ActionButton from "components/atoms/ActionButton";
import Button from "components/atoms/Button";
import InputField from "components/atoms/InputField";
import TableDataComponent from "components/organisms/TableData";
import React from "react";
import { useEffect } from "react";
import { useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import { useSelector } from "react-redux";
import FlickIdentification from "services/FlickIdentification";
import {
  errorToastNotif,
  setDataToLocalStorage,
  successToastNotif,
} from "utils/common";
import { PageMode } from "utils/constant";
import { useHistory } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

const FlickIdentificationService = new FlickIdentification();

const QRCodeModal = ({ isOpen, onClose, coordinate }) => {
  if (!isOpen) return null;
  const qrValue = coordinate ? `https://www.google.com/maps?q=${coordinate}` : "";
  
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        background: 'white', padding: '20px', borderRadius: '8px',
        textAlign: 'center', minWidth: '300px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <h4 style={{ marginBottom: '20px' }}>QR Code Google Maps</h4>
        {qrValue ? (
          <div style={{ marginBottom: '20px' }}>
            <QRCodeSVG value={qrValue} size={200} />
          </div>
        ) : (
          <p>Koordinat tidak tersedia.</p>
        )}
        <p style={{ marginBottom: '20px', color: '#666' }}>{coordinate}</p>
        <Button onClick={onClose} variant="primary" size="normal">Tutup</Button>
      </div>
    </div>
  );
};

const TableComponent = ({ changeMode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [qrModalState, setQrModalState] = useState({ isOpen: false, coordinate: "" });
  const [dataState, setDataState] = useState({
    page: 1,
    limit: 10,
    data: [],
    totalData: 0,
    currentPage: 1,
    nextPage: null,
    previousPage: null,
  });

  const { currentUser } = useSelector((state) => state.authReducer);
  const history = useHistory();

  const fetchFlickIdentificationData = async () => {
    setIsLoading(true);
    try {
      let searchBy = `fasyankes_id${
        searchKeyword !== "" ? `,urbanvillage_id` : ""
      }`;
      let searchValue = `${currentUser?.fasyankes_id}${
        searchKeyword !== "" ? `,${searchKeyword}` : ""
      }`;

      const response = await FlickIdentificationService.getAll({
        page: dataState.page,
        limit: dataState.limit,
        searchBy,
        searchValue,
      });

      setDataState((prevState) => ({
        ...prevState,
        data: response.vector_larva_identifications,
        limit: response.limit,
        currentPage: response.currentPage,
        totalData: response.total,
        nextPage: response.nextPage,
        previousPage: response.previousPage,
      }));
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFlickIdentificationData();
  }, [dataState.page, searchKeyword]);

  const onPageChange = (page) => {
    setDataState((prevState) => ({
      ...prevState,
      page: page,
    }));
  };

  const onEditData = (item) => {
    setDataToLocalStorage("SELECTED_ITEM", JSON.stringify(item));
    changeMode(PageMode.edit);
  };

  const onRemoveData = (id) => {
    confirmAlert({
      title: "Apakah anda yakin ingin menghapus data?",
      message: "Data akan dihapus secara permanen",
      buttons: [
        {
          label: "Ya",
          onClick: async () => {
            await FlickIdentificationService.remove(id)
              .then((res) => {
                fetchFlickIdentificationData();
                successToastNotif("Data Identifikasi Jentik Berhasil Dihapus");
              })
              .catch((err) => {
                console.log(err);
                errorToastNotif(err);
              });
          },
        },
        {
          label: "Tidak",
        },
      ],
    });
  };

  const openQrModal = (coordinate) => {
    setQrModalState({ isOpen: true, coordinate });
  };

  const closeQrModal = () => {
    setQrModalState({ isOpen: false, coordinate: "" });
  };

  const columns = [
    {
      header: "Aksi",
      accessorKey: "action",
      cell: ({ row }) => (
        <div className="button-actions">
          <ActionButton
            onClick={() => onEditData(row.original)}
            btnType={"edit"}
          />
          <ActionButton
            onClick={() => onRemoveData(row.original.id)}
            btnType={"delete"}
          />
          <ActionButton
            onClick={() =>
              history.push(
                `/data-intervensi-perindukan-nyamuk/${row.original.id}`
              )
            }
            btnType={"intervensi"}
            label={"Tambah Kohort"}
          />
          <ActionButton
            onClick={() => openQrModal(row.original.koordinat_terakhir)}
            btnType={"map"}
          />
        </div>
      ),
    },
    {
      header: "Desa",
      accessorKey: "urbanvillage_id",
      cell: ({ row }) => row.original.urbanvillage_id?.split("|")[1] || "-",
    },
    {
      header: "Dusun",
      accessorKey: "sub_village",
    },
    {
      header: "Nama TP",
      accessorKey: "name_breeding_place",
    },
    {
      header: "Jenis TP Awal",
      accessorKey: "type_of_breeding_place"
    },
    {
      header: "Petugas/Tim Awal",
      accessorKey: "officer_team",
      cell: ({ row }) => row.original.officer_team || row.original.petugas_awal || "-",
    },
    {
      header: "Tanggal Survei Terakhir",
      accessorKey: "latest_intervention_date",
      cell: ({ row }) => row.original.tanggal_terakhir || "-",
    },
    {
      header: "Status TP Terakhir",
      accessorKey: "latest_intervention_status",
      cell: ({ row }) => row.original.status_terakhir || "-",
    },
    {
      header: "Jumlah Kunjungan/Survei",
      accessorKey: "interventions_count",
      cell: ({ row }) => row.original.jumlah_kunjungan || "0",
    },
  ];

  const tableInstance = useReactTable({
    columns,
    data: dataState?.data,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <h1 className="page-title">
        Data Identifikasi Jentik & Kohort
      </h1>
      <div
        className="action-wrapper"
        style={{ marginTop: "24px", marginBottom: "24px" }}
      >
        <div className="action-button">
          {currentUser?.mode === "Online" && (
            <Button
              variant={"primary"}
              size={"normal"}
              onClick={() => changeMode(PageMode.add)}
            >
              Tambah Data
            </Button>
          )}
        </div>
        <div className="action-search">
          <InputField
            placeholder={"Cari berdasarkan nama desa..."}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
            }}
            value={searchKeyword}
          />
        </div>
      </div>

      {/* Table data component */}
      <TableDataComponent
        columnLength={columns.length}
        tableInstance={tableInstance}
        onPageChange={onPageChange}
        loading={isLoading}
        currentPage={dataState?.currentPage}
        limit={dataState?.limit}
        nextPage={dataState?.nextPage}
        previousPage={dataState?.previousPage}
        total={dataState?.totalData}
      />

      <QRCodeModal 
        isOpen={qrModalState.isOpen} 
        onClose={closeQrModal} 
        coordinate={qrModalState.coordinate} 
      />
    </div>
  );
};

export default TableComponent;
